interface CurrencySettings {
  CURRENCY?: string;
  CURRENCY_SYMBOLS?: Record<string, string>;
  EXCHANGE_RATES?: {
    rates?: string;
  };
}

let currencySettings: CurrencySettings = {};
const STORAGE_KEY = 'shop_widget_currency';

export function setCurrencySettings(settings: CurrencySettings) {
  currencySettings = settings;
}

function isKnownCurrency(cur: string | null): cur is string {
  if (!cur) return false;
  const symbols = currencySettings.CURRENCY_SYMBOLS || {};
  return cur in symbols;
}

function readStoredCurrency(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const cur = window.localStorage.getItem(STORAGE_KEY)?.toUpperCase() || null;
    return isKnownCurrency(cur) ? cur : null;
  } catch {
    return null;
  }
}

export function readStoredCurrencyRaw(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const cur = window.localStorage.getItem(STORAGE_KEY)?.toUpperCase() || null;
    return cur && /^[A-Z]{3,8}$/.test(cur) ? cur : null;
  } catch {
    return null;
  }
}

function writeStoredCurrency(cur: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, cur);
  } catch {
    // ignore storage errors
  }
}

/** Override CURRENCY from URL ?cur= so widget matches page currency after reload */
export function applyCurrencyFromUrl(): void {
  if (typeof window === 'undefined') return;
  const m = window.location.search.match(/[?&]cur=([^&]+)/i);
  const cur = m ? m[1].toUpperCase() : null;
  if (isKnownCurrency(cur)) {
    currencySettings = { ...currencySettings, CURRENCY: cur };
    writeStoredCurrency(cur);
    return;
  }
  const stored = readStoredCurrency();
  if (stored) {
    currencySettings = { ...currencySettings, CURRENCY: stored };
  }
}

export function getCurrencySettings(): CurrencySettings {
  return currencySettings;
}

export function getCurrentCurrency(): string {
  return currencySettings.CURRENCY || readStoredCurrency() || 'USD';
}

export function setSelectedCurrency(cur: string): void {
  const normalized = cur.toUpperCase();
  currencySettings = { ...currencySettings, CURRENCY: normalized };
  writeStoredCurrency(normalized);
}

export function formatPrice(value: string | number | undefined, decimals = 2): string {
  if (!value) return '$' + (0).toFixed(decimals);

  const numValue = typeof value === 'string' ? parseFloat(value.toString().replace(/[^0-9.-]/g, '')) : value;

  const currency = getCurrentCurrency();
  const currencySymbols = currencySettings.CURRENCY_SYMBOLS || {};
  const exchangeRatesJson = currencySettings.EXCHANGE_RATES?.rates;

  let exchangeRates: Record<string, number> = {};
  if (exchangeRatesJson) {
    try {
      exchangeRates = JSON.parse(exchangeRatesJson);
    } catch (e) {
      // ignore
    }
  }
  
  const currencySymbol = currencySymbols[currency] || '$';
  const rate = exchangeRates[currency] || 1;

  const convertedValue = numValue * rate;

  return currencySymbol + convertedValue.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function applyCurrencyToDocument(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('[data-sl-price]').forEach((el) => {
    const raw = el.getAttribute('data-sl-price');
    if (!raw) return;
    const decimals = Number(el.getAttribute('data-sl-price-decimals') || '2');
    const prefix = el.getAttribute('data-sl-price-prefix') || '';
    const suffix = el.getAttribute('data-sl-price-suffix') || '';
    el.textContent = prefix + formatPrice(raw, Number.isFinite(decimals) ? decimals : 2) + suffix;
  });

  root.querySelectorAll<HTMLElement>('[data-sl-price-range]').forEach((el) => {
    const min = Number(el.getAttribute('data-sl-price-min') || '0');
    const max = Number(el.getAttribute('data-sl-price-max') || '0');
    const minList = Number(el.getAttribute('data-sl-price-min-list') || '0');
    const mode = el.getAttribute('data-sl-price-range') || 'from_to';
    const fromLabel = el.getAttribute('data-sl-price-range-from') || 'From';
    const wasLabel = el.getAttribute('data-sl-price-range-was') || 'was';
    if (min <= 0 && max <= 0) return;
    if (mode === 'from_label') {
      let value = fromLabel + ' ' + formatPrice(min > 0 ? min : max, 2);
      if (minList > min) value += ' (' + wasLabel + ' ' + formatPrice(minList, 2) + ')';
      el.textContent = value;
      return;
    }
    if (mode === 'from_to' && max > min && min > 0) {
      el.textContent = formatPrice(min, 2) + ' - ' + formatPrice(max, 2);
      return;
    }
    el.textContent = formatPrice(min > 0 ? min : max, 2);
  });
}
